import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { hasCompletedOnboarding } from '@core/auth/onboarding-access';
import { AUTH_ENTRY_URL } from '@core/auth/auth-entry.constants';
import { AuthSessionService } from '@core/auth/auth-session.service';
import { AuthService } from '@features/auth/public-api';
import { isStaffEmailVerified } from '@features/auth/utils/staff-email-verified.util';
import { catchError, map, of } from 'rxjs';

/**
 * Ruta de verificación de email staff: autenticado y aún sin verificar.
 * Si ya verificó, continúa a onboard o home tenant.
 */
export const verifyEmailGuard: CanActivateFn = () => {
  const authSession = inject(AuthSessionService);
  const authService = inject(AuthService);
  const router = inject(Router);

  if (!authSession.isAuthenticated()) {
    return router.createUrlTree(AUTH_ENTRY_URL);
  }

  const nextAfterVerified = (user: NonNullable<ReturnType<AuthSessionService['user']>>) =>
    router.createUrlTree([hasCompletedOnboarding(user) ? '/dashboard' : '/onboard']);

  const decide = (user: NonNullable<ReturnType<AuthSessionService['user']>>) => {
    if (isStaffEmailVerified(user)) {
      return nextAfterVerified(user);
    }
    return true;
  };

  const user = authSession.user();
  if (user !== null) {
    return decide(user);
  }

  return authService.loadCurrentUser().pipe(
    map((loaded) => decide(loaded)),
    catchError(() => of(router.createUrlTree(AUTH_ENTRY_URL)))
  );
};
